/** Cadence CRUD queries — list, get, templates, tags, deleted, send preferences. */

import {
  CADENCE_FIELDS,
  CADENCE_EVENT_FIELDS,
  CADENCE_EDGE_FIELDS,
  AUTO_REMOVAL_RULE_FIELDS,
  SUCCESS_METRIC_FIELDS,
} from './fragments';

export const CADENCE_LIST_QUERY = `
  query CadenceList($pagination: CadencePaginationInput, $filter: CadenceListFilterInput) {
    cadenceList(pagination: $pagination, filter: $filter) {
      totalCount
      edges {
        cursor
        node {
          ${CADENCE_FIELDS}
          events {
            ${CADENCE_EVENT_FIELDS}
          }
          edges {
            ${CADENCE_EDGE_FIELDS}
          }
          successMetrics {
            ${SUCCESS_METRIC_FIELDS}
          }
          autoRemovalRules {
            ${AUTO_REMOVAL_RULE_FIELDS}
          }
        }
      }
      cadences {
        ${CADENCE_FIELDS}
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const CADENCES_PICKER_QUERY = `
  query Cadences {
    cadences {
      id
      name
      cadenceType
      isTemplate
      ownerUserId
      displayStatusLabel
      enrollmentLocked
    }
  }
`;

export const CADENCES_QUERY = `
  query Cadences {
    cadences {
      ${CADENCE_FIELDS}
      events {
        ${CADENCE_EVENT_FIELDS}
      }
      edges {
        ${CADENCE_EDGE_FIELDS}
      }
      autoRemovalRules {
        ${AUTO_REMOVAL_RULE_FIELDS}
      }
      successMetrics {
        ${SUCCESS_METRIC_FIELDS}
      }
    }
  }
`;

export const CADENCE_QUERY = `
  query Cadence($id: ID!) {
    cadence(id: $id) {
      ${CADENCE_FIELDS}
      events {
        ${CADENCE_EVENT_FIELDS}
      }
      edges {
        ${CADENCE_EDGE_FIELDS}
      }
      autoRemovalRules {
        ${AUTO_REMOVAL_RULE_FIELDS}
      }
      successMetrics {
        ${SUCCESS_METRIC_FIELDS}
      }
      linkedProposals {
        id
        proposalId
      }
    }
  }
`;

/**
 * Cadences created from a blueprint. Unlike CADENCES_QUERY/CADENCE_LIST_QUERY,
 * this returns every instance to anyone who can see the blueprint itself —
 * canView/canEdit on each node reflect the viewer's actual permission on that
 * specific cadence, so the caller can show it but gate opening it.
 */
export const CADENCE_TEMPLATE_INSTANCES_QUERY = `
  query CadenceTemplateInstances($templateId: ID!) {
    cadenceTemplateInstances(templateId: $templateId) {
      ${CADENCE_FIELDS}
    }
  }
`;

export const CADENCE_TAGS_QUERY = `
  query CadenceTags($search: String) {
    cadenceTags(search: $search)
  }
`;

/** Unified org-wide tag registry — covers cadences, email templates, and signatures. */
export const ORG_TAGS_QUERY = `
  query OrgTags($search: String) {
    orgTags(search: $search)
  }
`;

/** Register a tag in the org-wide registry (upsert, normalizes to lowercase). */
export const REGISTER_TAG_MUTATION = `
  mutation RegisterCadenceTag($name: String!) {
    registerCadenceTag(name: $name)
  }
`;

export const DELETED_CADENCES_QUERY = `
  query DeletedCadences {
    deletedCadences {
      ${CADENCE_FIELDS}
      deletedAt
      events {
        ${CADENCE_EVENT_FIELDS}
      }
      edges {
        ${CADENCE_EDGE_FIELDS}
      }
      successMetrics {
        ${SUCCESS_METRIC_FIELDS}
      }
    }
  }
`;

export const CADENCE_SEND_PREFERENCES_QUERY = `
  query CadenceSendPreferences($cadenceId: ID) {
    cadenceSendPreferences(cadenceId: $cadenceId) {
      id
      cadenceId
      preferredSendStart
      preferredSendEnd
      timezone
      sendMonday
      sendTuesday
      sendWednesday
      sendThursday
      sendFriday
      sendSaturday
      sendSunday
      version
    }
  }
`;

/**
 * Fetch the org's sending domain status via the cadences service.
 * The backend returns an uppercase GraphQL enum (CONFIGURED | PENDING | NOT_CONFIGURED | ERROR);
 * useDomainStatus normalizes these to lowercase before exposing them to consumers.
 *
 * This goes through the cadences backend (server-to-server to Outreach) rather
 * than calling the Outreach REST API directly, because the direct endpoint
 * (/v1/postmark/domain) is admin-gated and returns 403 for non-owner users.
 */
export const ORG_DOMAIN_STATUS_QUERY = `
  query OrgDomainStatus {
    orgDomainStatus {
      status
      domainName
      defaultSendingEmail
    }
  }
`;
